import styled from 'styled-components';

import { color } from '@creditornot/cb-ingredients/design-tokens';
import { Modal } from '@creditornot/cbt-modal';
import { ModalContent } from 'common/components/cookbook/Modals/ModalContent';
import { ModalHeader } from 'common/components/cookbook/Modals/ModalHeader';
import { Stack } from 'common/components/cookbook/Stack';
import { Typography as T } from 'common/components/cookbook/Typography/Typography';
import { useFormatMessage } from 'common/hooks/useFormatMessage';

import { OverrideModalHeaderSubsection } from './OverrideModalHeaderSubsection';
import { OverridesSummary } from '../api-types';
import { OverrideDetailSection } from './OverrideDetailSection';
import { getEndedOverrides, getImpactingOverrides, getUpcomingOverrides } from './utils';

const ListHeader = styled(T.subheadline).attrs({ as: 'h4' })`
  padding: 24px;
  margin: 0;
  border-top: 1px solid ${color.border};
  border-bottom: 1px solid ${color.border};

  &:first-of-type {
    border-top: none;
  }
`;

type Props = {
  overrideToView: OverridesSummary | undefined;
  isOpen: boolean;
  onClose: () => void;
};

export const OverrideSummaryDetailsModal = ({ overrideToView, onClose, isOpen }: Props) => {
  const format = useFormatMessage();
  const impacting = (overrideToView && getImpactingOverrides(overrideToView.overrides)) ?? [];
  const upcoming = (overrideToView && getUpcomingOverrides(overrideToView.overrides)) ?? [];
  const ended = (overrideToView && getEndedOverrides(overrideToView.overrides)) ?? [];

  return (
    <Modal
      sizePreset="sideEndSheet:large"
      open={isOpen && overrideToView !== undefined}
      onDismiss={onClose}
      ContentComponent={null}
    >
      <ModalHeader
        onDismiss={onClose}
        subtitle={
          overrideToView !== undefined ? (
            <OverrideModalHeaderSubsection overrideSummary={overrideToView} />
          ) : null
        }
      >
        {format('forecasting.product_overrides')}
      </ModalHeader>
      <ModalContent padding="none">
        <>
          {impacting.length !== 0 && (
            <>
              <ListHeader>{format('forecasting.status:IMPACTS_FORECAST')}</ListHeader>
              <Stack>
                {impacting.map((override, i) => {
                  return <OverrideDetailSection override={override ?? []} key={i} />;
                })}
              </Stack>
            </>
          )}

          {upcoming.length !== 0 && (
            <>
              <ListHeader>{format('forecasting.status:UPCOMING')}</ListHeader>
              <Stack>
                {upcoming.map((override, i) => {
                  return <OverrideDetailSection override={override} key={i} />;
                })}
              </Stack>
            </>
          )}

          {ended.length !== 0 && (
            <>
              <ListHeader>{format('forecasting.last_ended')}</ListHeader>
              <Stack>
                {ended.slice(0, 1).map((override, i) => {
                  return <OverrideDetailSection override={override} key={i} />;
                })}
              </Stack>
            </>
          )}
        </>
      </ModalContent>
    </Modal>
  );
};
